import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { requestMarketingToken } from '../firebase';
import {
    QrCode, MessageCircle, Scan,
    Bell, Tag, TrendingUp,
    Zap, Rocket, Eye, CheckCircle, XCircle, Gift, Star, Check, Lock, ChevronLeft, ChevronRight
} from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import LandingVoiceGuide from '../components/LandingVoiceGuide';
import '../styles/landing.css';

/* ─── Animated counter hook ─────────────────────────────────── */
const useCountUp = (target, duration = 1500, startOnVisible = true) => {
    const [count, setCount] = useState(0);
    const [started, setStarted] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!startOnVisible) { setStarted(true); return; }
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setStarted(true); observer.disconnect(); } },
            { threshold: 0.3 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [startOnVisible]);

    useEffect(() => {
        if (!started) return;
        let start = 0;
        const step = Math.ceil(target / (duration / 16));
        const timer = setInterval(() => {
            start += step;
            if (start >= target) { setCount(target); clearInterval(timer); }
            else setCount(start);
        }, 16);
        return () => clearInterval(timer);
    }, [started, target, duration]);

    return [count, ref];
};

/* ─── FAQ Item ───────────────────────────────────────────────── */
const FAQItem = ({ question, answer }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className={`faq-item ${open ? 'open' : ''}`} onClick={() => setOpen(!open)}>
            <div className="faq-question">
                <span>{question}</span>
                <span className="faq-icon">{open ? '−' : '+'}</span>
            </div>
            {open && <div className="faq-answer">{answer}</div>}
        </div>
    );
};

const BASE = 'https://menu.searchisend.in';

const LandingPage = () => {
    const { scrollYProgress } = useScroll();
    const yHeroParallax = useTransform(scrollYProgress, [0, 1], [0, 200]);
    const yFloat1 = useTransform(scrollYProgress, [0, 1], [0, -50]);
    const yFloat2 = useTransform(scrollYProgress, [0, 1], [0, 50]);

    const [rest1Ref, animRef1] = useCountUp(1200);
    const [rest2Ref, animRef2] = useCountUp(50000);
    const [rest3Ref, animRef3] = useCountUp(99);

    const defaultMockReviews = [
        { name: 'Ramesh Patel', role: 'Owner, Spice Route Café', rating: 5, comment: 'We used to spend ₹2,000 every month reprinting our menu. Now I update prices from my phone in seconds. The digital QR ordering feature is a game changer — no more missed calls!' },
        { name: 'Sunita Sharma', role: 'Owner, The Curry House', rating: 5, comment: 'My customers were always asking for photos of dishes. Now they can see beautiful images on their own phone. Our average order value went up by 35% since we switched.' },
        { name: 'Amit Verma', role: 'Manager, Bites & Bytes Café', rating: 5, comment: 'The setup took honestly less than 2 minutes. I used the AI scanner on my old menu and it filled everything in. Absolutely fantastic. Worth every rupee.' }
    ];

    const [reviews, setReviews] = useState(defaultMockReviews);
    const [activeReviewIndex, setActiveReviewIndex] = useState(0);
    const [autoPlay, setAutoPlay] = useState(true);

    // Write a Review Modal State
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewForm, setReviewForm] = useState({ name: '', role: '', email: '', phone: '', rating: 5, comment: '' });
    const [reviewHoverRating, setReviewHoverRating] = useState(0);
    const [reviewSubmitting, setReviewSubmitting] = useState(false);
    const [reviewStatus, setReviewStatus] = useState({ type: '', message: '' });

    // Dynamic Pricing State
    const [saasPlans, setSaasPlans] = useState([]);
    const [trialDays, setTrialDays] = useState(7);
    const [isYearlyBilling, setIsYearlyBilling] = useState(true);

    useEffect(() => {
        const fetchPricing = async () => {
            try {
                // Fetch dynamic plans from v2 API
                const res = await fetch(`${BASE}/apimenu/v2/v2_public_plans.php`);
                const data = await res.json();
                if (data.success && data.plans) {
                    setSaasPlans(data.plans.filter(p => p.is_monthly_active === 1 || p.is_yearly_active === 1));
                    if (data.trial_days !== undefined) {
                        setTrialDays(data.trial_days);
                    }
                }
            } catch (err) {
                console.error("Error fetching pricing:", err);
            }
        };
        fetchPricing();
        fetchApprovedReviews();
    }, []);

    useEffect(() => {
        if (!autoPlay || reviews.length <= 1) return;
        const interval = setInterval(() => {
            setActiveReviewIndex(prev => (prev + 1) % reviews.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [autoPlay, reviews.length]);

    const fetchApprovedReviews = async () => {
        try {
            const res = await fetch(`${BASE}/apimenu/landing_actions.php?action=fetch_reviews`);
            const data = await res.json();
            if (data.success && data.reviews && data.reviews.length > 0) {
                setReviews(data.reviews);
            }
        } catch (err) {
            console.error("Error fetching approved reviews:", err);
        }
    };

    const handlePrevReview = () => {
        setActiveReviewIndex(prev => (prev - 1 + reviews.length) % reviews.length);
    };

    const handleNextReview = () => {
        setActiveReviewIndex(prev => (prev + 1) % reviews.length);
    };

    const handleReviewFormChange = (e) => {
        const { name, value } = e.target;
        setReviewForm(prev => ({ ...prev, [name]: value }));
    };

    const handleReviewSubmit = async (e) => {
        e.preventDefault();
        setReviewSubmitting(true);
        setReviewStatus({ type: '', message: '' });

        if (!reviewForm.name || !reviewForm.email || !reviewForm.phone || !reviewForm.comment) {
            setReviewStatus({ type: 'error', message: 'Please fill in all required fields.' });
            setReviewSubmitting(false);
            return;
        }

        try {
            const res = await fetch(`${BASE}/apimenu/landing_actions.php?action=submit_review`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'submit_review',
                    name: reviewForm.name,
                    role: reviewForm.role,
                    email: reviewForm.email,
                    phone: reviewForm.phone,
                    rating: reviewForm.rating,
                    comment: reviewForm.comment
                })
            });
            const data = await res.json();
            if (data.success) {
                setReviewStatus({ type: 'success', message: data.message });
                setReviewForm({ name: '', role: '', email: '', phone: '', rating: 5, comment: '' });
                setTimeout(() => {
                    setShowReviewModal(false);
                    setReviewStatus({ type: '', message: '' });
                }, 3000);
            } else {
                setReviewStatus({ type: 'error', message: data.message || 'Submission failed.' });
            }
        } catch (err) {
            setReviewStatus({ type: 'error', message: 'Failed to submit review. Check your connection.' });
        } finally {
            setReviewSubmitting(false);
        }
    };

    // Marketing Lead Magnet State
    const [showMarketingPrompt, setShowMarketingPrompt] = useState(false);
    const [promptActionLoading, setPromptActionLoading] = useState(false);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        document.title = "Digital QR Menu for Restaurants | SIE QR Menu – Free 7-Day Trial";

        const setMeta = (name, content, prop = false) => {
            const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
            let el = document.querySelector(sel);
            if (!el) {
                el = document.createElement('meta');
                prop ? el.setAttribute('property', name) : el.setAttribute('name', name);
                document.head.appendChild(el);
            }
            el.setAttribute('content', content);
        };

        setMeta('description', 'Create a stunning digital QR menu for your restaurant in 30 seconds. Zero-commission digital ordering, coupon management, push notifications & more. Free 7-day trial.');
        setMeta('keywords', 'digital menu for restaurants, qr code menu, restaurant menu app, online menu, digital menu India');
        setMeta('og:title', 'SIE QR Menu – #1 Digital QR Menu for Restaurants in India', true);
        setMeta('og:description', 'Replace paper menus with a stunning digital QR menu. Free trial, no credit card required.', true);
        setMeta('og:type', 'website', true);
        setMeta('og:url', 'https://menu.searchisend.in', true);
        setMeta('twitter:card', 'summary_large_image');
        setMeta('twitter:title', 'SIE QR Menu – Digital QR Menu for Restaurants');

        // Extended Schema.org
        const schema = [
            {
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "SIE QR Menu",
                "operatingSystem": "Web, Android, iOS",
                "applicationCategory": "BusinessApplication, FoodOrderingApplication",
                "description": "SaaS platform to create contactless QR menus, enable zero-commission digital ordering, manage restaurant coupons, and run targeted customer push notifications.",
                "logo": "https://menu.searchisend.in/logo512.png",
                "url": "https://menu.searchisend.in",
                "offers": { "@type": "Offer", "price": "499.00", "priceCurrency": "INR" },
                "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "312" },
                "featureList": [
                    "Contactless QR Code Menu Generator",
                    "Zero-Commission Digital Order Integration",
                    "Dynamic Coupon & Discount Engine",
                    "Instant Subscriber Push Notifications",
                    "Direct Software Table Order Tracker"
                ]
            },
            {
                "@context": "https://schema.org",
                "@type": "Organization",
                "name": "Search is End!",
                "url": "https://searchisend.in",
                "logo": "https://menu.searchisend.in/logo512.png",
                "description": "Search is End! is a leading software solutions provider delivering modern digital tools for restaurants and local businesses, including SIE QR Menu.",
                "sameAs": [
                    "https://menu.searchisend.in",
                    "https://searchisend.com"
                ]
            },
            {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    { "@type": "Question", "name": "Is there a free trial?", "acceptedAnswer": { "@type": "Answer", "text": "Yes! SIE QR Menu offers a 7-day free trial with no credit card required." } },
                    { "@type": "Question", "name": "Do I need a developer to set it up?", "acceptedAnswer": { "@type": "Answer", "text": "Not at all. You can set up your digital menu in under 30 seconds with our step-by-step wizard." } }
                ]
            }
        ];
        let script = document.getElementById('schema-org');
        if (!script) { script = document.createElement('script'); script.type = 'application/ld+json'; script.id = 'schema-org'; document.head.appendChild(script); }
        script.text = JSON.stringify(schema);

        // --- 30 Second Lead Magnet Timer ---
        const hasPrompted = localStorage.getItem('lp_marketing_prompted');
        if (!hasPrompted) {
            const timer = setTimeout(() => {
                setShowMarketingPrompt(true);
            }, 30000); // 30 seconds
            return () => {
                clearTimeout(timer);
            };
        }

    }, []);

    const handleMarketingSubscribe = async () => {
        setError('');
        if (!window.Notification) {
            setError("This browser doesn't support push notifications. Please try Chrome or Edge.");
            return;
        }
        setPromptActionLoading(true);
        try {
            const success = await requestMarketingToken();
            if (success) {
                localStorage.setItem('lp_marketing_prompted', 'true');
                setIsSubscribed(true);
                setTimeout(() => {
                    setShowMarketingPrompt(false);
                }, 3500);
            } else {
                setError("Subscription failed. Please check your browser notification permission settings.");
            }
        } catch (err) {
            setError("Error: " + (err.message || "Unknown error occurred"));
            console.error(err);
        } finally {
            setPromptActionLoading(false);
        }
    };

    const dismissMarketingPrompt = () => {
        localStorage.setItem('lp_marketing_prompted', 'true');
        setShowMarketingPrompt(false);
    };

    const faqs = [
        { question: "Is there a free trial?", answer: trialDays > 0 ? `Yes! SIE QR Menu offers a full ${trialDays}-day free trial. No credit card required and no strings attached. If it's not right for you, just walk away.` : `No, we do not currently offer a free trial, but our plans are very affordable with no long-term contracts.` },
        { question: "Do I need any technical skills to set it up?", answer: "Zero skills needed. Our setup wizard guides you step-by-step. Most restaurants go live within 30 seconds of signing up." },

        { question: "Can I update my menu prices anytime?", answer: "Absolutely. You can change prices, add items, or hide categories in real-time from your phone. No reprinting needed." },
        { question: "What if my internet goes down?", answer: "Your customer-facing menu is served from a fast CDN (Content Delivery Network). Your menu stays accessible even during local internet issues." },
        { question: "I have multiple restaurant branches. Does it support that?", answer: "Yes! Our Pro plan supports multiple branches under one account. Each branch gets its own unique menu, QR code, and analytics." },
    ];

    return (
        <>
            {/* ── 2. HERO SECTION ── */}
            <section className="hero-section">
                <motion.div
                    className="hero-content"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                >
                    <div className="hero-tag"><Zap size={16} style={{ display: 'inline', verticalAlign: 'text-bottom', marginRight: '4px' }} /> Trusted by 1,200+ restaurants across India</div>
                    <h1 className="hero-title">
                        Stop Losing Customers<br />
                        To <span>Outdated Paper Menus</span>
                    </h1>
                    <p className="hero-subtitle">
                        Give your restaurant a premium digital QR menu in 30 seconds — with zero-commission digital ordering, live offers, and push notifications. No printing. No waiting. No commission.
                    </p>
                    <div className="hero-cta">
                        <Link to="/signup" className="premium-btn-primary pulse-glow">
                            <Rocket size={18} style={{ display: 'inline', verticalAlign: 'text-bottom', marginRight: '6px' }} /> {trialDays > 0 ? `Start Your Free ${trialDays}-Day Trial` : `Get Started Now`}
                        </Link>
                        <a href="https://menu.searchisend.in/demo-kitchen" className="premium-btn-secondary" target="_blank" rel="noopener noreferrer">
                            <Eye size={18} style={{ display: 'inline', verticalAlign: 'text-bottom', marginRight: '6px' }} /> Try Live Menu Demo
                        </a>
                    </div>
                    <p className="hero-disclaimer">No credit card required. Setup in 30 seconds. Cancel anytime.</p>
                </motion.div>

                <motion.div
                    className="hero-mockup-area"
                    style={{ y: yHeroParallax }}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                >
                    <div className="phone-frame">
                        <div className="phone-screen">
                            <div className="phone-header-bar">
                                <div className="phone-dot red"></div>
                                <div className="phone-dot yellow"></div>
                                <div className="phone-dot green"></div>
                                <div style={{ fontSize: '0.6rem', color: '#888', marginLeft: '4px' }}>menu.searchisend.in</div>
                            </div>
                            <div className="phone-iframe-wrapper">
                                <iframe
                                    src="https://menu.searchisend.in/demokitchen"
                                    className="phone-iframe"
                                    title="Live Menu Preview"
                                ></iframe>
                            </div>
                        </div>
                    </div>

                    <motion.div className="hero-floater top-right" style={{ y: yFloat1 }}>
                        <span><CheckCircle size={14} style={{ display: 'inline', color: '#10b981', verticalAlign: 'middle' }} /></span><span>12 orders live</span>
                    </motion.div>
                    <motion.div className="hero-floater bottom-left" style={{ y: yFloat2 }}>
                        <span><Star size={14} fill="#fbbf24" color="#fbbf24" style={{ display: 'inline', verticalAlign: 'text-bottom', marginRight: '4px' }} />4.9</span><span>Avg. rating</span>
                    </motion.div>
                </motion.div>
            </section>

            {/* ── 3. SOCIAL PROOF BAR ── */}
            <section className="social-proof-bar">
                <p className="proof-label">Loved by restaurants across India</p>
                <div className="proof-stats">
                    <div className="proof-stat" ref={animRef1}>
                        <span className="stat-number">{rest1Ref.toLocaleString()}+</span>
                        <span className="stat-desc">Restaurants Active</span>
                    </div>
                    <div className="proof-divider"></div>
                    <div className="proof-stat" ref={animRef2}>
                        <span className="stat-number">{rest2Ref.toLocaleString()}+</span>
                        <span className="stat-desc">Digital Orders Placed</span>
                    </div>
                    <div className="proof-divider"></div>
                    <div className="proof-stat" ref={animRef3}>
                        <span className="stat-number">{rest3Ref}%</span>
                        <span className="stat-desc">Customer Satisfaction</span>
                    </div>
                </div>
            </section>

            {/* ── 3.5 VIDEO DEMO SECTION ── */}
            <section className="laptop-mockup-section">
                <motion.div
                    className="section-header"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false, amount: 0.3 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                    <div className="section-tag">Watch It In Action</div>
                    <h2>See How <span>SIE QR Menu</span> Grows Your Business</h2>
                    <p>Take a 2-minute tour of the full dashboard and ordering experience.</p>
                </motion.div>
                <div className="laptop-wrapper">
                    <div className="laptop-screen">
                        <iframe
                            src="https://www.youtube.com/embed/aW0i7JwZyOk?autoplay=0&mute=1&rel=0"
                            title="YouTube video player"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                        ></iframe>
                    </div>
                    <div className="laptop-base"></div>
                </div>
            </section>

            {/* ── 4. PAIN → SOLUTION ── */}
            <section className="pain-section">
                <div className="pain-container">
                    <div className="pain-block">
                        <h2 className="pain-title"><XCircle size={24} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} />The Old Way Is Costing You</h2>
                        <ul className="pain-list">
                            <li>Spending ₹3,000/month reprinting menus for price changes</li>
                            <li>Paying 25–30% commission to Zomato & Swiggy on every order</li>
                            <li>Customers leave because paper menus look unprofessional</li>
                            <li>Zero data on what dishes are most popular</li>
                            <li>No way to run real-time flash offers during slow hours</li>
                        </ul>
                    </div>
                    <div className="pain-arrow">→</div>
                    <div className="solution-block">
                        <h2 className="solution-title"><CheckCircle size={24} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '8px' }} />The SIE QR Menu Way</h2>
                        <ul className="solution-list">
                            <li>Update prices from your phone in <strong>10 seconds</strong></li>
                            <li>100% of the order revenue goes to <strong>you</strong>, zero commission</li>
                            <li>Premium, app-like menu experience that <strong>wows customers</strong></li>
                            <li>Live analytics: see your best-sellers in <strong>real time</strong></li>
                            <li>Flash offer popups to boost sales <strong>on command</strong></li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* ── 5. FEATURES SHOWCASE ── */}
            <section className="features-section" id="features">
                <motion.div
                    className="section-header"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false, amount: 0.3 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                    <div className="section-tag">Everything You Need</div>
                    <h2>A Powerful Platform, <br /><span>Built for Restaurants</span></h2>
                    <p>All the tools a modern restaurant owner needs to increase revenue, reduce cost, and delight customers.</p>
                </motion.div>
                <div className="feature-grid">
                    {[
                        { icon: <QrCode size={24} />, title: 'Instant QR Code Menu', desc: 'Generate a beautiful, scannable QR code. Print it or display it digitally. Customers scan and browse instantly — no app download needed.' },
                        { icon: <MessageCircle size={24} />, title: 'Zero-Commission Orders', desc: 'Every digital order goes straight to you, with no middleman. Keep 100% of your revenue, every single time.' },
                        { icon: <Scan size={24} />, title: 'AI Menu Scanner', desc: 'Take a photo of your existing paper menu and our AI will digitize it automatically — categories, items, prices, and all.' },
                        { icon: <Bell size={24} />, title: 'Push Notifications', desc: 'Send flash deals, festive offers, or new item alerts directly to the phones of customers who visited your menu before.' },
                        { icon: <Tag size={24} />, title: 'Offers & Coupons', desc: 'Create targeted discount popups, "Happy Hour" deals, and loyalty coupons that appear automatically — without any coding.' },
                        { icon: <TrendingUp size={24} />, title: 'Live Analytics', desc: 'See which dishes are most viewed, track unique visitors, and understand customer behavior to double down on what works.' },
                    ].map((f, i) => (
                        <motion.div
                            key={f.title}
                            className="feature-card"
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: false, amount: 0.3 }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            whileHover={{ scale: 1.05, rotateY: 5, rotateX: 5 }}
                            style={{ transformPerspective: 1000 }}
                        >
                            <div className="feature-icon">{f.icon}</div>
                            <h3>{f.title}</h3>
                            <p>{f.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ── 6. LEAD MAGNET CTA (mid-page) ── */}
            <section className="lead-magnet-section">
                <div className="lead-magnet-inner">
                    <div className="lead-magnet-content">
                        {trialDays > 0 && <span className="lead-magnet-tag"><Gift size={16} style={{ display: 'inline', verticalAlign: 'text-bottom', marginRight: '4px' }} /> Free for {trialDays} Days</span>}
                        <h2>See SIE QR Menu Working <br />On Your Restaurant — <em>Right Now</em></h2>
                        <p>No setup fee. No credit card. Just a premium digital menu live for your customers in 30 seconds.</p>
                    </div>
                    <Link to="/signup" className="premium-btn-primary large-btn">
                        <Rocket size={20} style={{ display: 'inline', verticalAlign: 'text-bottom', marginRight: '8px' }} /> Activate My Free Menu
                    </Link>
                </div>
            </section>

            {/* ── 7. HOW IT WORKS ── */}
            <section className="how-section">
                <motion.div
                    className="section-header"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false, amount: 0.3 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                    <div className="section-tag">Simple 3-Step Setup</div>
                    <h2>From Signup to <span>Live Menu</span> in 30 Seconds</h2>
                </motion.div>
                <div className="steps-grid">
                    {[
                        { num: '01', icon: 'fa-user-plus', title: 'Sign Up Free', desc: 'Create your account in 10 seconds. No credit card. Just your name, restaurant name, and a password.' },
                        { num: '02', icon: 'fa-utensils', title: 'Build Your Menu', desc: 'Add dishes manually, use our AI scanner on your old paper menu, or pick a starter template for your cuisine type.' },
                        { num: '03', icon: 'fa-qrcode', title: 'Go Live & Profit', desc: 'Download your QR code PDF, print and place it on tables. Customers scan, browse, and order effortlessly.' },
                    ].map((s, i) => (
                        <motion.div
                            key={s.num}
                            className="step-card"
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: false, amount: 0.3 }}
                            transition={{ duration: 0.5, delay: i * 0.15, ease: "easeOut" }}
                        >
                            <div className="step-number">{s.num}</div>
                            <div className="step-icon"><i className={`fas ${s.icon}`}></i></div>
                            <h3>{s.title}</h3>
                            <p>{s.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* ── 8. TESTIMONIALS ── */}
            <section className="testimonials-section">
                <motion.div
                    className="section-header"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false, amount: 0.3 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                    <div className="section-tag">Real Results</div>
                    <h2>Restaurants Love <span>SIE QR Menu</span></h2>
                </motion.div>


                {reviews.length > 0 && (
                    <motion.div
                        className="testimonials-carousel"
                        onMouseEnter={() => setAutoPlay(false)}
                        onMouseLeave={() => setAutoPlay(true)}
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: false, amount: 0.3 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                    >
                        <button className="carousel-nav prev" onClick={handlePrevReview} aria-label="Previous Review">
                            <ChevronLeft size={24} />
                        </button>

                        <div className="carousel-track-container">
                            <div className="carousel-track" style={{ transform: `translateX(-${activeReviewIndex * 100}%)` }}>
                                {reviews.map((t, idx) => (
                                    <div key={idx} className="carousel-slide-item">
                                        <div className="testimonial-card" style={{ margin: '0 auto', maxWidth: '600px' }}>
                                            <div className="testimonial-stars">
                                                {Array.from({ length: t.rating }).map((_, i) => (
                                                    <Star key={i} size={16} fill="#fbbf24" color="#fbbf24" style={{ display: 'inline', verticalAlign: 'text-bottom', marginRight: '2px' }} />
                                                ))}
                                            </div>
                                            <p className="testimonial-text">"{t.comment}"</p>
                                            <div className="testimonial-author">
                                                <div className="author-avatar">{t.name.charAt(0)}</div>
                                                <div>
                                                    <div className="author-name">{t.name}</div>
                                                    <div className="author-role">{t.role}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button className="carousel-nav next" onClick={handleNextReview} aria-label="Next Review">
                            <ChevronRight size={24} />
                        </button>

                        <div className="carousel-dots">
                            {reviews.map((_, idx) => (
                                <button
                                    key={idx}
                                    className={`carousel-dot ${idx === activeReviewIndex ? 'active' : ''}`}
                                    onClick={() => setActiveReviewIndex(idx)}
                                    aria-label={`Go to slide ${idx + 1}`}
                                />
                            ))}
                        </div>
                    </motion.div>
                )}

                <div style={{ marginTop: '3rem', textAlign: 'center' }}>
                    <button
                        className="premium-btn-secondary"
                        onClick={() => setShowReviewModal(true)}
                        style={{ padding: '12px 30px', borderRadius: '50px', fontSize: '1rem', fontWeight: 600 }}
                    >
                        Write a Review
                    </button>
                </div>
            </section>

            {/* ── 9. PRICING ── */}
            <section className="pricing-section" id="pricing">
                <motion.div
                    className="section-header"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false, amount: 0.3 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                    <div className="section-tag">No Hidden Fees</div>
                    <h2>Simple, <span>Honest Pricing</span></h2>
                    <p>Start free. Upgrade when you're ready — and save 30% by going yearly.</p>
                </motion.div>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
                    <div className="billing-toggle" style={{ display: 'inline-flex', background: 'var(--bg-dark)', padding: '4px', borderRadius: '30px', alignItems: 'center' }}>
                        <button
                            onClick={() => setIsYearlyBilling(false)}
                            style={{ padding: '8px 20px', borderRadius: '26px', border: 'none', background: !isYearlyBilling ? '#fff' : 'transparent', color: !isYearlyBilling ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer', boxShadow: !isYearlyBilling ? '0 2px 8px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.3s' }}
                        >Monthly</button>
                        <button
                            onClick={() => setIsYearlyBilling(true)}
                            style={{ padding: '8px 20px', borderRadius: '26px', border: 'none', background: isYearlyBilling ? '#fff' : 'transparent', color: isYearlyBilling ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: 600, cursor: 'pointer', boxShadow: isYearlyBilling ? '0 2px 8px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.3s', display: 'flex', alignItems: 'center', gap: '5px' }}
                        >Yearly <span style={{ background: 'var(--success)', color: '#fff', fontSize: '0.65rem', padding: '2px 6px', borderRadius: '10px' }}>Save 30%</span></button>
                    </div>
                </div>

                <div className="pricing-grid">
                    {saasPlans.length === 0 ? (
                        <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '4rem' }}>
                            <div className="loader inline-block"></div>
                            <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Loading latest plans...</p>
                        </div>
                    ) : (
                        saasPlans.filter(p => isYearlyBilling ? p.is_yearly_active === 1 : p.is_monthly_active === 1).map((plan, idx) => (
                            <motion.div
                                key={plan.id}
                                className={`pricing-card ${plan.is_popular === 1 ? 'popular' : ''}`}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: false, amount: 0.3 }}
                                transition={{ duration: 0.5, delay: idx * 0.1, ease: "easeOut" }}
                            >
                                {plan.is_popular === 1 && <div className="popular-badge">🔥 MOST POPULAR</div>}

                                <h3>{plan.plan_name}</h3>
                                <div className="pricing-price">
                                    ₹{isYearlyBilling ? plan.price_yearly : plan.price_monthly}
                                    <span>/{isYearlyBilling ? 'year' : 'month'}</span>
                                </div>
                                <p className="pricing-tagline">{plan.tagline || `Great for growing restaurants.`}</p>

                                <ul className="pricing-list">
                                    {plan.features_included.map((f, i) => (
                                        <li key={`inc-${i}`}><Check size={18} color="var(--lp-orange)" style={{ flexShrink: 0, marginTop: '2px' }} /> {f}</li>
                                    ))}
                                    {plan.features_excluded.map((f, i) => (
                                        <li key={`exc-${i}`} className="disabled"><Lock size={16} style={{ flexShrink: 0, marginTop: '2px' }} /> {f}</li>
                                    ))}
                                </ul>

                                <Link to="/signup" className={plan.is_popular === 1 ? "premium-btn-primary full-width-btn" : "premium-btn-secondary full-width-btn"}>
                                    {trialDays > 0 ? `Start ${trialDays}-Day Trial` : `Get Started`}
                                </Link>
                            </motion.div>
                        ))
                    )}
                </div>
            </section>

            {/* ── 10. FAQ ── */}
            <section className="faq-section" id="faq">
                <motion.div
                    className="section-header"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false, amount: 0.3 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                    <div className="section-tag">Got Questions?</div>
                    <h2>Everything You Need to <span>Know</span></h2>
                </motion.div>
                <div className="faq-list">
                    {faqs.map(f => <FAQItem key={f.question} {...f} />)}
                </div>
            </section>

            {/* ── 11. FINAL URGENCY CTA ── */}
            <section className="final-cta-section">
                <div className="final-cta-inner">
                    <h2>Your Competitors Are Already<br /><span>Using Digital Menus.</span></h2>
                    <p>Don't let them get ahead. Launch your SIE QR Menu today{trialDays > 0 ? ` — free for ${trialDays} days` : ''}, no credit card required.</p>
                    <Link to="/signup" className="premium-btn-primary large-btn pulse-glow">
                        <Rocket size={20} style={{ display: 'inline', verticalAlign: 'text-bottom', marginRight: '8px' }} /> {trialDays > 0 ? `Start My Free Trial Now` : `Get Started Now`}
                    </Link>
                    <p className="final-disclaimer">Trusted by 1,200+ restaurants · No credit card required · Cancel anytime</p>
                </div>
            </section>

            {/* ── 13. MARKETING LEAD MAGNET PROMPT ── */}
            {showMarketingPrompt && (
                <div className="marketing-prompt-overlay">
                    <div className="marketing-prompt-card slide-up">
                        {isSubscribed ? (
                            <>
                                <div className="prompt-header">
                                    <span className="prompt-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', boxShadow: '0 0 15px rgba(16, 185, 129, 0.2)', animation: 'none' }}>
                                        <CheckCircle size={20} />
                                    </span>
                                    <button className="prompt-close" onClick={dismissMarketingPrompt}>&times;</button>
                                </div>
                                <div className="prompt-body">
                                    <h3>🎉 You're Subscribed!</h3>
                                    <p>Thank you for subscribing. You will now receive instant updates on new features, success strategies, and seasonal tips directly in your browser.</p>
                                </div>
                                <div className="prompt-actions">
                                    <button className="prompt-btn-primary" onClick={dismissMarketingPrompt}>
                                        Great!
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="prompt-header">
                                    <span className="prompt-icon"><Bell size={20} /></span>
                                    <button className="prompt-close" onClick={dismissMarketingPrompt}>&times;</button>
                                </div>
                                <div className="prompt-body">
                                    <h3>Get Live Growth & Order Alerts</h3>
                                    <p>Enable notifications to receive instant updates on new features, success strategies, and seasonal tips to scale your restaurant.</p>
                                    {error && <div className="error-message" style={{ color: '#ef4444', fontSize: '0.85rem', marginTop: '10px', fontWeight: 'bold' }}>{error}</div>}
                                </div>
                                <div className="prompt-actions">
                                    <button
                                        className="prompt-btn-primary"
                                        onClick={handleMarketingSubscribe}
                                        disabled={promptActionLoading}
                                    >
                                        {promptActionLoading ? 'Subscribing...' : 'Subscribe to Alerts'}
                                    </button>
                                    <button className="prompt-btn-link" onClick={dismissMarketingPrompt}>Maybe later</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* ── WRITE A REVIEW MODAL ── */}
            {showReviewModal && (
                <div className="review-modal-overlay">
                    <div className="review-modal">
                        <div className="review-modal-header">
                            <h3 style={{ margin: 0, fontWeight: 800 }}>Submit Your Review</h3>
                            <button className="review-modal-close" onClick={() => setShowReviewModal(false)}>&times;</button>
                        </div>

                        <form onSubmit={handleReviewSubmit}>
                            {reviewStatus.message && (
                                <div style={{
                                    padding: '12px', borderRadius: '8px', marginBottom: '1.25rem', fontSize: '0.9rem', fontWeight: 600,
                                    background: reviewStatus.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                                    color: reviewStatus.type === 'success' ? '#22c55e' : '#ef4444',
                                    border: `1px solid ${reviewStatus.type === 'success' ? '#22c55e' : '#ef4444'}`
                                }}>
                                    {reviewStatus.message}
                                </div>
                            )}

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div className="review-form-group">
                                    <label className="review-form-label">Full Name *</label>
                                    <input type="text" name="name" className="review-input" value={reviewForm.name} onChange={handleReviewFormChange} required placeholder="Ramesh Patel" />
                                </div>
                                <div className="review-form-group">
                                    <label className="review-form-label">Role / Restaurant Name</label>
                                    <input type="text" name="role" className="review-input" value={reviewForm.role} onChange={handleReviewFormChange} placeholder="Owner, Spice Cafe" />
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div className="review-form-group">
                                    <label className="review-form-label">Email Address *</label>
                                    <input type="email" name="email" className="review-input" value={reviewForm.email} onChange={handleReviewFormChange} required placeholder="ramesh@example.com" />
                                </div>
                                <div className="review-form-group">
                                    <label className="review-form-label">Phone Number *</label>
                                    <input type="tel" name="phone" className="review-input" value={reviewForm.phone} onChange={handleReviewFormChange} required placeholder="919876543210" />
                                </div>
                            </div>

                            <div className="review-form-group">
                                <label className="review-form-label">Rating *</label>
                                <div style={{ display: 'flex', gap: '6px', cursor: 'pointer', margin: '4px 0' }}>
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <span
                                            key={star}
                                            onClick={() => setReviewForm(prev => ({ ...prev, rating: star }))}
                                            onMouseEnter={() => setReviewHoverRating(star)}
                                            onMouseLeave={() => setReviewHoverRating(0)}
                                            style={{ display: 'flex', alignItems: 'center' }}
                                        >
                                            <Star
                                                size={24}
                                                fill={(reviewHoverRating || reviewForm.rating) >= star ? '#fbbf24' : 'none'}
                                                color={(reviewHoverRating || reviewForm.rating) >= star ? '#fbbf24' : '#888'}
                                            />
                                        </span>
                                    ))}
                                </div>
                            </div>

                            <div className="review-form-group">
                                <label className="review-form-label">Review Comment *</label>
                                <textarea name="comment" className="review-textarea" rows="4" value={reviewForm.comment} onChange={handleReviewFormChange} required placeholder="Tell us about your experience using SIE QR Menu..."></textarea>
                            </div>

                            <button
                                type="submit"
                                className="prompt-btn-primary"
                                style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', fontWeight: 700, fontSize: '1rem', marginTop: '0.5rem' }}
                                disabled={reviewSubmitting}
                            >
                                {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
            {/* ── LANDING VOICE SALES GUIDE ── */}
            <LandingVoiceGuide />
        </>
    );
};

export default LandingPage;

